import type { BuiltInTool, BuiltInToolError, JsonSchema } from './types'

/**
 * #1844: publish a role or a team the enterprise owns to the catalog, for a person's own agent over the API and the OS
 * MCP server, as the portal and the API already can. One tool per route: `POST /v1/catalog/roles` and
 * `POST /v1/catalog/share/teams/:teamId`, both under `catalog:write`.
 *
 * Neither lists `run` or `chat` among its callers, so a task that declares one is refused when it is saved
 * (`tool_not_run_callable`). A task reaches them only through a bound connection to the OS's own MCP server, where
 * every write that server serves is must-gate (#1858), as the task write tools are.
 *
 * Withdrawing or changing a published role or team is not here (#1844's scope): sharing a team again brings its entry
 * up to date, and a published role is only its portable name and slug.
 */

const PLAN_REFUSED: BuiltInToolError = {
    code: 'catalog_publish_not_in_plan',
    when: 'The enterprise’s plan does not include catalog publishing. The refusal says so in a sentence and names /billing. Nothing is written.',
    handling: 'tool_error',
}

const ROLE_SLUG_TAKEN: BuiltInToolError = {
    code: 'catalog_role_slug_taken',
    when: 'The catalog already holds a public role with this role’s slug. Public role slugs are global, so a role is published once; rename the role’s slug to publish it as another.',
    handling: 'tool_error',
}

const ROLE_NOT_FOUND: BuiltInToolError = {
    code: 'role_not_found',
    when: '`sourceRoleId` names no role in this enterprise, another enterprise’s included.',
    handling: 'tool_error',
}

const SOURCE_ROLE_REQUIRED: BuiltInToolError = {
    code: 'source_role_required',
    when: '`sourceRoleId` is missing or empty.',
    handling: 'tool_error',
}

const TEAM_ROLE_SLUG_TAKEN: BuiltInToolError = {
    code: 'catalog_role_slug_taken',
    when: 'A public role with one of the team’s role slugs comes from another publisher. The refusal names them as `slugs`; rename those roles and publish again. Nothing is written.',
    handling: 'tool_error',
}

const TEAM_HAS_NO_ROLES: BuiltInToolError = {
    code: 'team_has_no_roles',
    when: 'No member of the team holds a role and none of its tasks runs as one, or `roleSlugs` leaves every role out, so a clone would have nothing to hold.',
    handling: 'tool_error',
}

const TEAM_NOT_FOUND: BuiltInToolError = {
    code: 'team_not_found',
    when: '`teamId` names no team in this enterprise, another enterprise’s included.',
    handling: 'tool_error',
}

const INVALID_CATALOG_TEAM: BuiltInToolError = {
    code: 'invalid_catalog_team',
    when: '`name` is longer than 200 characters, or `description` longer than 280.',
    handling: 'tool_error',
}

const INVALID_TEAM_ROLES: BuiltInToolError = {
    code: 'invalid_team_roles',
    when: '`roleSlugs` is not a list of slugs, or names a role the team does not have.',
    handling: 'tool_error',
}

const INVALID_KIND_OF_WORK: BuiltInToolError = {
    code: 'invalid_kind_of_work',
    when: '`kindOfWork` is not one lowercase word (letters, digits and underscores, at most 64 characters), or is something_else.',
    handling: 'tool_error',
}

const INVALID_BUSINESS_FUNCTION: BuiltInToolError = {
    code: 'invalid_business_function',
    when: '`businessFunction` is not one of support, marketing, sales, operations or engineering.',
    handling: 'tool_error',
}

const text = (description: string): JsonSchema => ({ type: 'string', description })

/** The words both publish tools end on: who may call them, and the only way a task reaches them. */
const UNDECLARABLE =
    'A task that declares this tool is refused when it is saved. A task reaches it only through a bound connection ' +
    'to the OS’s own MCP server, with a gate naming the tool, as with every write that server serves.'

export const OS_CATALOG_PUBLISH_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.publish_role',
        family: 'os.org',
        summary: 'Publish one of the enterprise’s roles to the catalog, by its portable name and slug.',
        description:
            'Publish one of the enterprise’s roles to the catalog, where any company can clone it. Pass sourceRoleId, ' +
            'the role’s id from os.list_roles. Only the role’s name and slug are published: its members stay with ' +
            'the enterprise, and its tasks are listed with it once each is published to the catalog itself (those ' +
            'already published join it at once). Public ' +
            'role slugs are global, so a slug the catalog already holds is refused (catalog_role_slug_taken). A plan ' +
            'without catalog publishing is refused with its reason (catalog_publish_not_in_plan) and nothing is ' +
            `written. ${UNDECLARABLE} The response is the public role, with its id and slug.`,
        inputSchema: {
            type: 'object',
            properties: {
                sourceRoleId: text('The role to publish, by its id from os.list_roles.'),
            },
            required: ['sourceRoleId'],
            additionalProperties: false,
        },
        returns: 'The public role: `id`, `slug`, `name`, `visibility` (`public`) and `ownerEnterpriseId`.',
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'unsafe',
        retryNote:
            'A repeat after a successful publish is refused (catalog_role_slug_taken): the catalog then holds the role. Read the catalog’s roles to see it there.',
        errors: [PLAN_REFUSED, ROLE_SLUG_TAKEN, ROLE_NOT_FOUND, SOURCE_ROLE_REQUIRED],
        api: { method: 'POST', path: '/v1/catalog/roles', scope: 'catalog:write' },
        examples: [{ input: { sourceRoleId: '5f0c1d2e-0000-4000-8000-000000000021' } }],
        public: true,
    },
    {
        name: 'os.publish_team',
        family: 'os.org',
        summary: 'Publish one of the enterprise’s teams to the catalog, with its roles, as the portal’s Share does.',
        description:
            'Publish one of the enterprise’s teams to the catalog, as Share on the team’s page does. Pass teamId, ' +
            'the team’s id from os.list_teams. The team’s roles the catalog does not hold yet are published first, ' +
            'then the team as one entry under its slug: a teammate slot per member carrying the roles they hold and ' +
            'nothing of the member, its KPIs, the cadence of each schedule on a task the catalog holds, and its goal. ' +
            'Its tasks travel once each is published to the catalog itself. roleSlugs names the roles that go; the ' +
            'rest are left out, and stay left out when the team is published again without roleSlugs. name and ' +
            'description are the words the ' +
            'catalog shows, and default to the team’s name and goal; kindOfWork and businessFunction file it. ' +
            'Publishing a team that has an entry brings that entry up to date instead of adding a second. Refused on ' +
            'a plan without catalog publishing (catalog_publish_not_in_plan), for a team with no roles ' +
            '(team_has_no_roles), and while another publisher holds a public role of one of its slugs ' +
            `(catalog_role_slug_taken, naming the slugs); nothing is written then. ${UNDECLARABLE} The response is ` +
            'the entry and the public roles it names.',
        inputSchema: {
            type: 'object',
            properties: {
                teamId: text('The team to publish, by its id from os.list_teams.'),
                roleSlugs: {
                    type: 'array',
                    items: { type: 'string' },
                    description:
                        'The slugs of the team’s roles that go to the catalog; the rest are left out. Leave it out to keep what was left out last time.',
                },
                name: text('The name the catalog shows, at most 200 characters. Leave it out for the team’s name.'),
                description: text(
                    'One line the catalog shows, at most 280 characters. Leave it out for the team’s goal.',
                ),
                kindOfWork: text(
                    'The kind of work setup offers the team for: one lowercase word, such as support. Leave it out for none.',
                ),
                businessFunction: {
                    type: 'string',
                    enum: ['support', 'marketing', 'sales', 'operations', 'engineering'],
                    description: 'The business function the catalog files the team under. Leave it out for none.',
                },
            },
            required: ['teamId'],
            additionalProperties: false,
        },
        returns:
            'The catalog entry as `entry` (`id`, `slug`, `name`, `teammates`), and the public roles it names as `roles`.',
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'unsafe',
        retryNote:
            'A repeat publishes the team again: its entry is brought up to date from the team as it stands, and no second entry is made.',
        errors: [
            PLAN_REFUSED,
            TEAM_ROLE_SLUG_TAKEN,
            TEAM_HAS_NO_ROLES,
            TEAM_NOT_FOUND,
            INVALID_CATALOG_TEAM,
            INVALID_TEAM_ROLES,
            INVALID_KIND_OF_WORK,
            INVALID_BUSINESS_FUNCTION,
        ],
        api: { method: 'POST', path: '/v1/catalog/share/teams/:teamId', scope: 'catalog:write' },
        examples: [
            {
                input: {
                    teamId: '5f0c1d2e-0000-4000-8000-000000000022',
                    name: 'Support desk',
                    kindOfWork: 'support',
                    businessFunction: 'support',
                },
            },
        ],
        public: true,
    },
]
